'use client';

import logoPrefeitura from '@/assets/logo-da-prefeitura-750x375.png';
import logoHosp from '@/assets/logohosp.png';
import { PawPrint, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface PetCardProps {
  cardNumber: string;
  ownerName: string;
}

const PetCardMiniature: React.FC<PetCardProps> = ({
  cardNumber = "",
  ownerName = ""
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const shineRef = useRef<HTMLDivElement>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const formattedCardNumber = cardNumber.length === 11
    ? cardNumber
        .replace(/\s+/g, "")
        .replace(/(\d{3})(\d{2})(\d{3})(\d{3})/g, "$1 $2 $3 $4")
        .trim()
    : cardNumber
        .replace(/\s+/g, "")
        .replace(/(\d{4})(?=\d)/g, "$1 ")
        .trim();

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.style.animation = 'fadeInScale 0.6s ease-out forwards';
    }
    if (shineRef.current) {
      shineRef.current.style.animation = 'shine 1.5s ease-out 0.5s forwards';
    }
  }, []);

  return (
    <>
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes shine {
          from {
            transform: translateX(-100vw) rotate(25deg);
          }
          to {
            transform: translateX(200vw) rotate(25deg);
          }
        }

        @keyframes slideInFromBottom {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .slide-in-bottom {
          animation: slideInFromBottom 0.3s ease-out;
        }
      `}</style>
      
      <div className="mt-5 mb-4 w-full px-2">
        <h3 className="text-lg font-bold mb-3 text-gray-800">Meu PetCard</h3>
        
        <div
          ref={cardRef}
          onClick={() => setIsSheetOpen(true)}
          className="relative w-full bg-blue-600 rounded-2xl p-4 overflow-hidden cursor-pointer shadow-lg active:scale-95 transition-transform"
          style={{ opacity: 0 }}
        >
          <div
            ref={shineRef}
            className="absolute -top-24 -left-5 -bottom-24 w-16 bg-white/20 transform -translate-x-full"
            style={{ transform: 'translateX(-100vw) rotate(25deg)' }}
          />
          
          <div className="absolute -top-10 -left-10 w-4/5 aspect-square bg-white/7 rounded-full" />

          <div className="relative z-10 h-full flex flex-col">
            <div className="flex justify-between items-start mb-1">
              <div className="flex-1">
                <div className="mb-1">
                  <img
                    src={logoHosp}
                    alt="Hospital Logo"
                    className="w-12 h-12 mb-1 object-contain"
                  />
                </div>
                <p className="text-white font-bold text-xs leading-tight drop-shadow-sm">
                  HOSPITAL MUNICIPAL
                </p>
                <p className="text-white font-bold text-lg -mt-1 drop-shadow-sm">
                  VETERINÁRIO
                </p>
              </div>
              <div className="flex-1 text-right">
                <p className="text-white font-bold text-xl drop-shadow-sm">
                  PETCARD
                </p>
                <p className="text-white font-semibold text-sm drop-shadow-sm">
                  SÃO LUÍS
                </p>
              </div>
            </div>

            <div className="flex-1 flex items-center my-5">
              <p className="text-white font-bold text-xl tracking-wider drop-shadow-sm">
                {formattedCardNumber}
              </p>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="flex-1 max-w-[60%]">
                <p className="text-white font-bold text-sm uppercase drop-shadow-sm leading-tight line-clamp-2 overflow-hidden">
                  {ownerName}
                </p>
              </div>
              <div className="flex items-center ml-2 flex-shrink-0 h-10">
                <img
                  src={logoPrefeitura}
                  alt="Logo da Prefeitura"
                  className="w-16 h-8 mr-1 object-contain"
                />
                <PawPrint size={24} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        {isSheetOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
            <div className="w-full h-full bg-blue-600 slide-in-bottom flex items-center justify-center relative">
              <button 
                onClick={() => setIsSheetOpen(false)}
                className="absolute top-4 right-4 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10"
              >
                <X size={24} className="text-white" />
              </button>
              
              <div className="relative bg-blue-600 rounded-2xl p-8 overflow-hidden rotate-90 origin-center scale-150">
                <div className="absolute -top-10 -left-10 w-4/5 aspect-square bg-white/7 rounded-full" />

                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1">
                      <div className="mb-2">
                        <img
                          src={logoHosp}
                          alt="Hospital Logo"
                          className="w-16 h-16 mb-2 object-contain"
                        />
                      </div>
                      <p className="text-white font-bold text-sm leading-tight drop-shadow-sm">
                        HOSPITAL MUNICIPAL
                      </p>
                      <p className="text-white font-bold text-2xl -mt-1 drop-shadow-sm">
                        VETERINÁRIO
                      </p>
                    </div>
                    <div className="flex-1 text-right">
                      <p className="text-white font-bold text-3xl drop-shadow-sm">
                        PETCARD
                      </p>
                      <p className="text-white font-semibold text-lg drop-shadow-sm">
                        SÃO LUÍS
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 flex items-center my-6">
                    <p className="text-white font-bold text-2xl tracking-wider drop-shadow-sm">
                      {formattedCardNumber}
                    </p>
                  </div>

                  <div className="flex justify-between items-center mt-4">
                    <div className="flex-1 max-w-[60%]">
                      <p className="text-white font-bold text-base uppercase drop-shadow-sm leading-tight break-words">
                        {ownerName}
                      </p>
                    </div>
                    <div className="flex items-center ml-2 flex-shrink-0 h-10">
                      <img
                        src={logoPrefeitura}
                        alt="Logo da Prefeitura"
                        className="w-16 h-8 mr-1 object-contain"
                      />
                      <PawPrint size={24} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const PetCardFullScreen: React.FC<PetCardProps> = ({
  cardNumber,
  ownerName,
}) => {
  const formattedCardNumber = cardNumber.length === 11
    ? cardNumber
        .replace(/\s+/g, "")
        .replace(/(\d{3})(\d{2})(\d{3})(\d{3})/g, "$1 $2 $3 $4")
        .trim()
    : cardNumber
        .replace(/\s+/g, "")
        .replace(/(\d{4})(?=\d)/g, "$1 ")
        .trim();

  return (
    <div className="fixed inset-0 bg-blue-600 flex items-center justify-center">
      <div className="relative w-full bg-blue-600 rounded-2xl p-4 overflow-hidden rotate-90 origin-center">
        <div className="absolute -top-10 -left-10 w-4/5 aspect-square bg-white/7 rounded-full" />

        <div className="relative z-10 h-full flex flex-col">
          <div className="flex justify-between items-start mb-1">
            <div className="flex-1">
              <div className="mb-1">
                <img
                  src={logoHosp}
                  alt="Hospital Logo"
                  className="w-12 h-12 mb-1 object-contain"
                />
              </div>
              <p className="text-white font-bold text-xs leading-tight drop-shadow-sm">
                HOSPITAL MUNICIPAL
              </p>
              <p className="text-white font-bold text-lg -mt-1 drop-shadow-sm">
                VETERINÁRIO
              </p>
            </div>
            <div className="flex-1 text-right">
              <p className="text-white font-bold text-xl drop-shadow-sm">
                PETCARD
              </p>
              <p className="text-white font-semibold text-sm drop-shadow-sm">
                SÃO LUÍS
              </p>
            </div>
          </div>

          <div className="flex-1 flex items-center my-5">
            <p className="text-white font-bold text-xl tracking-wider drop-shadow-sm">
              {formattedCardNumber}
            </p>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="flex-1 max-w-[60%]">
              <p className="text-white font-bold text-sm uppercase drop-shadow-sm leading-tight line-clamp-2 overflow-hidden">
                {ownerName}
              </p>
            </div>
            <div className="flex items-center ml-2 flex-shrink-0 h-10">
              <img
                src={logoPrefeitura}
                alt="Logo da Prefeitura"
                className="w-16 h-8 mr-1 object-contain"
              />
              <PawPrint size={24} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PetCardFullScreen, PetCardMiniature };
export default PetCardMiniature;